import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { PublicListing } from '@/contexts/SearchContext';
import PropertyCard from './PropertyCard';

interface PropertyGridProps {
  listings: PublicListing[];
  loading?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  onLoadMore?: () => void;
  onListingClick?: (listing: PublicListing) => void;
  onFavoriteClick?: (listingId: string, isFavorite: boolean) => void;
  onRetry?: () => void;
  error?: string | null;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateAction?: React.ReactNode;
  showInstantBook?: boolean;
  variant?: 'default' | 'compact';
  infiniteScroll?: boolean;
  loadMoreThreshold?: number;
}

const PropertyGrid: React.FC<PropertyGridProps> = ({
  listings,
  loading = false,
  hasMore = false,
  totalCount = 0,
  onLoadMore,
  onListingClick,
  onFavoriteClick,
  onRetry,
  error,
  emptyStateTitle = "No properties found",
  emptyStateDescription = "Try adjusting your search criteria or browse all available properties.",
  emptyStateAction,
  showInstantBook = true,
  variant = 'default',
  infiniteScroll = true,
  loadMoreThreshold = 200,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Handle infinite scroll
  useEffect(() => {
    if (!infiniteScroll || !onLoadMore || !hasMore || loading) return;

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !isLoadingMore) {
        setIsLoadingMore(true);
        onLoadMore();
      }
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: `${loadMoreThreshold}px`,
      threshold: 0.1,
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [infiniteScroll, onLoadMore, hasMore, loading, isLoadingMore, loadMoreThreshold]);

  // Reset loading more state when new data arrives
  useEffect(() => {
    if (!loading) {
      setIsLoadingMore(false);
    }
  }, [loading]);

  // Handle manual load more
  const handleLoadMore = useCallback(() => {
    if (onLoadMore && !loading && !isLoadingMore) {
      setIsLoadingMore(true);
      onLoadMore();
    }
  }, [onLoadMore, loading, isLoadingMore]);

  // Get responsive grid columns (values are column spans out of 12)
  const getGridColumns = () => {
    if (variant === 'compact') {
      return { xs: 6, sm: 4, md: 3, lg: 2, xl: 2 };
    }
    // Mobile: single column, Tablet: 2-col, Desktop: 3-col, Large: 4-col
    return { xs: 12, sm: 6, md: 4, lg: 3, xl: 3 };
  };

  const gridColumns = getGridColumns();

  // Loading skeleton
  const renderLoadingSkeleton = () => {
    const skeletonCount = isMobile ? 4 : isTablet ? 6 : 8;
    
    return (
      <Grid container spacing={isMobile ? 2 : 3}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <Grid item {...gridColumns} key={`skeleton-${index}`}>
            <PropertyCard
              listing={{} as PublicListing}
              loading={true}
              variant={variant}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  // Error state
  if (error && listings.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Alert
          severity="error"
          sx={{
            mb: 3,
            maxWidth: 400,
          }}
        >
          {error}
        </Alert>
        
        {onRetry && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            Try again
          </Button>
        )}
      </Box>
    );
  }

  // Empty state
  if (!loading && listings.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
          textAlign: 'center',
        }}
      >
        <SearchIcon
          sx={{
            fontSize: 64,
            color: 'text.secondary',
            mb: 2,
          }}
        />
        
        <Typography
          variant="h5"
          fontWeight={600}
          color="text.primary"
          sx={{ mb: 1 }}
        >
          {emptyStateTitle}
        </Typography>
        
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 3,
            maxWidth: 400,
            lineHeight: 1.6,
          }}
        >
          {emptyStateDescription}
        </Typography>
        
        {emptyStateAction}
      </Box>
    );
  }

  // Loading state (initial load)
  if (loading && listings.length === 0) {
    return renderLoadingSkeleton();
  }

  return (
    <Box>
      {/* Results Grid */}
      <Grid container spacing={isMobile ? 2 : 3}>
        {listings.map((listing) => (
          <Grid item {...gridColumns} key={listing.listingId}>
            <PropertyCard
              listing={listing}
              onCardClick={onListingClick}
              onFavoriteClick={onFavoriteClick}
              showInstantBook={showInstantBook}
              variant={variant}
            />
          </Grid>
        ))}
      </Grid>

      {/* Load More Section */}
      {(hasMore || isLoadingMore) && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
            mt: 2,
          }}
        >
          {isLoadingMore || loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading more properties...
              </Typography>
            </Box>
          ) : (
            !infiniteScroll && hasMore && (
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                }}
              >
                Load more properties
              </Button>
            )
          )}
        </Box>
      )}

      {/* End of results indicator */}
      {!hasMore && listings.length > 0 && totalCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 4,
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing all {totalCount} {totalCount === 1 ? 'property' : 'properties'}
          </Typography>
        </Box>
      )}

      {/* Error during load more */}
      {error && listings.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            mt: 2,
          }}
        >
          <Alert
            severity="error"
            action={
              onRetry && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onRetry}
                >
                  Retry
                </Button>
              )
            }
            sx={{ maxWidth: 400 }}
          >
            {error}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default PropertyGrid;